import { useMemo } from 'react';
import { Users, DollarSign } from 'lucide-react';
import { formatCurrencyNoDecimals } from '@/utils/formatters';
import type { Fund } from '@/types/fund';
import type { InvestorDetail } from '@/types/investor';

interface FundInvestorsSummaryCardsProps {
  fund: Fund;
  fundInvestors: InvestorDetail[];
}

export function FundInvestorsSummaryCards({
  fund,
  fundInvestors,
}: FundInvestorsSummaryCardsProps) {
  const stats = useMemo(() => {
    let totalCommitments = 0;
    let activeCount = 0;
    let pendingCount = 0;

    for (const investor of fundInvestors) {
      const rel = investor.fund_relationships.find(
        (r) => r.fund_id === fund.id
      );
      if (rel) {
        totalCommitments += rel.commitment_amount;
        if (rel.status === 'ACTIVE') activeCount++;
        else pendingCount++;
      }
    }

    return { totalCommitments, activeCount, pendingCount };
  }, [fundInvestors, fund.id]);

  const items = [
    {
      icon: Users,
      label: 'Total Investors',
      value: fundInvestors.length.toString(),
    },
    {
      icon: DollarSign,
      label: 'Total Commitments',
      value: formatCurrencyNoDecimals(
        stats.totalCommitments,
        fund.base_currency
      ),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 sm:flex sm:items-center sm:gap-8 py-2">
      {items.map((item, index) => (
        <div key={item.label} className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <item.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {item.label}
              </span>
              <span className="text-lg font-semibold text-foreground truncate">
                {item.value}
              </span>
            </div>
          </div>
          {index < items.length - 1 && (
            <div className="hidden sm:block h-8 w-px bg-border flex-shrink-0" />
          )}
        </div>
      ))}
    </div>
  );
}
